import {
  Armchair,
  Box,
  Building2,
  Eye,
  FileText,
  ImageIcon,
  LayoutGrid,
  MousePointerClick,
  PenLine,
  Sparkles,
  Grid2x2,
  Sofa,
  Trees,
  TreePine,
  Upload,
  WandSparkles,
} from 'lucide-react';
import bohoOriginalPreview from '../assets/design_tools/Boho_Original_L.webp';
import modernPreview from '../assets/design_tools/Modern_L.webp';
import bohoFurnPreview from '../assets/design_tools/Boho_Furn_L.webp';
import floorPlan3dPreview from '../assets/design_tools/2d_3d_L.webp';
import floorPlanRoomPreview from '../assets/design_tools/2d_rooom_L.webp';
import aiFloorPlanPreview from '../assets/design_tools/AI_2D_L.webp';
import drawFloorPlanPreview from '../assets/design_tools/Draw_2D_L.webp';
import exteriorOldPreview from '../assets/design_tools/exterior_old_L.webp';
import exteriorNewPreview from '../assets/design_tools/exterior_new_L.webp';
import { PUBLIC_TOOL_SEO } from './publicToolSeoPages';
import { after0, before1, after1 } from '../assets/home_page_assets';

export const DESIGN_TOOL_TAB_ORDER = ['room-interior', 'exterior', 'floor-plan'];

export const CATEGORY_TOOL_HUBS = {
  'room-interior': {
    id: 'room-interior',
    tabLabel: 'Interior Tools',
    tabShortLabel: 'Interior',
    tabIcon: Sofa,
    path: '/projects/new?tab=room-interior',
    seoTitle: 'Room Interior Design Tools | MyRoomsAI',
    seoDescription:
      'Redesign your room interiors with AI. Change furniture, update style, or create a completely new look.',
    heading: 'Interior Design Tools',
    description:
      'Redesign your room interiors with AI. Change furniture, update style, or create a completely new look.',
    bannerSrc: after0,
    bannerAlt: 'Room interior design preview',
    gridCols: 2,
    pageClassName: 'tool-hub-page room-interior-tool-hub-page',
    tools: [
      {
        id: 'full-decoration',
        title: 'Room Decorator',
        description: 'Get a completely new interior design with a full room transformation.',
        route: PUBLIC_TOOL_SEO.roomDecoration.path,
        cta: 'Start Design',
        badge: null,
        icon: Sparkles,
        previewSrc: modernPreview,
        previewAlt: 'Full decoration preview',
        beforeImage: bohoOriginalPreview,
        afterImage: modernPreview,
        flow: [
          { icon: Upload, label: 'Upload photo' },
          { icon: Sparkles, label: 'AI redesign' },
          { icon: Sparkles, label: 'New interior' },
        ],
      },
      {
        id: 'furniture-change',
        title: 'Furniture Changer',
        description:
          'Change furniture while keeping the room structure.',
        route: PUBLIC_TOOL_SEO.furnitureReplacement.path,
        cta: 'Start Design',
        badge: null,
        icon: Armchair,
        previewSrc: bohoFurnPreview,
        previewAlt: 'Furniture change preview',
        beforeImage: bohoOriginalPreview,
        afterImage: bohoFurnPreview,
        flow: [
          { icon: Upload, label: 'Upload photo' },
          { icon: Armchair, label: 'Change furniture' },
          { icon: Sparkles, label: 'New look' },
        ],
      },
      {
        id: 'interior-3d-model',
        title: '3D Model to Realistic Interior',
        description:
          'Convert 3D interior model screenshots into photorealistic interior images while preserving structure and camera angle.',
        route: PUBLIC_TOOL_SEO.interior3dModelToRealistic.path,
        cta: 'Generate Realistic Interior',
        badge: null,
        icon: Box,
        previewSrc: modernPreview,
        previewAlt: '3D model to realistic interior preview',
        beforeImage: bohoOriginalPreview,
        afterImage: modernPreview,
        flow: [
          { icon: Upload, label: 'Upload 3D screenshot' },
          { icon: Box, label: 'AI convert' },
          { icon: Sparkles, label: 'Realistic interior' },
        ],
      },
      {
        id: 'light-renovation',
        hidden: true,
        title: 'Light Renovation Upgrade',
        description:
          'Refresh colors, materials, and décor of your room without a full redesign.',
        route: PUBLIC_TOOL_SEO.furnitureReplacement.path,
        cta: 'Start Design',
        badge: null,
        icon: WandSparkles,
        previewSrc: modernPreview,
        previewAlt: 'Light renovation preview',
        beforeImage: before1,
        afterImage: after1,
        flow: [
          { icon: Upload, label: 'Upload photo' },
          { icon: Sparkles, label: 'Refresh' },
          { icon: Sparkles, label: 'Updated room' },
        ],
      },
    ],
  },
  exterior: {
    id: 'exterior',
    tabLabel: 'Exterior Tools',
    tabShortLabel: 'Exterior',
    tabIcon: TreePine,
    path: '/projects/new?tab=exterior',
    seoTitle: 'Exterior Design Tools | MyRoomsAI',
    seoDescription:
      'Enhance gardens, outdoor spaces, and building exteriors with AI.',
    heading: 'Exterior Design Tools',
    description:
      'Enhance gardens, outdoor spaces, and building exteriors with AI.',
    bannerSrc: '/images/real-transformations/2-a.webp',
    bannerAlt: 'Exterior design preview',
    gridCols: 2,
    pageClassName: 'tool-hub-page exterior-tool-hub-page',
    tools: [
      {
        id: 'garden-design',
        title: 'Garden Designer',
        description:
          'Upload a garden or outdoor photo and generate a beautiful landscape design.',
        route: PUBLIC_TOOL_SEO.gardenDesign.path,
        cta: 'Start Design',
        comingSoon: false,
        badge: null,
        icon: Trees,
        previewSrc: '/images/real-transformations/2-a.webp',
        previewAlt: 'Garden design preview',
        flow: [
          { icon: Upload, label: 'Upload photo' },
          { icon: Trees, label: 'Choose style' },
          { icon: Sparkles, label: 'New garden' },
        ],
      },
      {
        id: 'building-exterior',
        title: 'Exterior Designer',
        description: 'Upload a building exterior photo and transform the facade style.',
        route: PUBLIC_TOOL_SEO.exteriorDesign.path,
        cta: 'Start Design',
        comingSoon: false,
        icon: Building2,
        previewSrc: exteriorNewPreview,
        previewAlt: 'Exterior renovation preview',
        beforeImage: exteriorOldPreview,
        afterImage: exteriorNewPreview,
        flow: [
          { icon: Upload, label: 'Upload photo' },
          { icon: Building2, label: 'Choose style' },
          { icon: Sparkles, label: 'New exterior' },
        ],
      },
    ],
  },
  'floor-plan': {
    id: 'floor-plan',
    tabLabel: 'Architecture AI Tools',
    tabShortLabel: '2D Floor Plan',
    tabIcon: Grid2x2,
    path: '/projects/new?tab=floor-plan',
    seoTitle: 'Floor Plan Tools | MyRoomsAI',
    seoDescription:
      'Create, convert, and visualize floor plans in 2D and 3D with AI.',
    heading: 'Floor Plan Tools',
    description:
      'Create, convert, and visualize floor plans in 2D and 3D with AI.',
    bannerSrc: '/images/real-transformations/3-a.webp',
    bannerAlt: 'Floor plan tools preview',
    gridCols: 2,
    pageClassName: 'tool-hub-page floor-plan-tool-hub-page',
    tools: [
      {
        id: 'floor-plan-3d',
        title: 'FloorPlan to 3D Image',
        description:
          'Upload a 2D floor plan and convert it into a 3D / 2.5D top-view image.',
        route: PUBLIC_TOOL_SEO.floorPlanTo3d.path,
        cta: 'Convert Plan',
        badge: null,
        icon: Box,
        previewSrc: floorPlan3dPreview,
        previewAlt: '2D to 3D floor plan preview',
        flow: [
          { icon: Upload, label: 'Upload floor plan' },
          { icon: Sparkles, label: 'AI' },
          { icon: Eye, label: '3D view' },
        ],
      },
      {
        id: 'floor-plan-room-preview',
        title: 'FloorPlan to Room Image',
        description:
          'Select a room from a floor plan and generate a realistic room preview.',
        route: PUBLIC_TOOL_SEO.floorPlanRoomRender.path,
        cta: 'Create Room Preview',
        badge: null,
        icon: ImageIcon,
        previewSrc: floorPlanRoomPreview,
        previewAlt: 'Floor plan room preview',
        flow: [
          { icon: Upload, label: 'Upload floor plan' },
          { icon: MousePointerClick, label: 'Select room' },
          { icon: Sparkles, label: 'Room preview' },
        ],
      },
      {
        id: 'floor-plan-create',
        title: 'FloorPlan AI Creator',
        description: 'Describe your home details and let AI generate a 2D floor plan.',
        route: PUBLIC_TOOL_SEO.floorPlanCreator.path,
        cta: 'Create Floor Plan',
        badge: null,
        icon: Grid2x2,
        previewSrc: aiFloorPlanPreview,
        previewAlt: '2D floor plan preview',
        flow: [
          { icon: FileText, label: 'Home details' },
          { icon: Sparkles, label: 'AI' },
          { icon: LayoutGrid, label: '2D floor plan' },
        ],
      },
      {
        id: 'draw-floor-plan',
        title: 'FloorPlan Designer',
        description: 'Draw your floor plan manually and customize the layout your way.',
        route: PUBLIC_TOOL_SEO.floorPlanDesigner.path,
        cta: 'Start Drawing',
        badge: null,
        previewBadge: 'free',
        icon: PenLine,
        previewSrc: drawFloorPlanPreview,
        previewAlt: 'Draw floor plan preview',
        flow: [
          { icon: PenLine, label: 'Draw 2D plan' },
          { icon: LayoutGrid, label: 'Edit layout' },
          { icon: Sparkles, label: 'Final plan' },
        ],
      },
    ],
  },
};

export const DESIGN_TOOL_CATEGORIES = DESIGN_TOOL_TAB_ORDER.map(
  (id) => CATEGORY_TOOL_HUBS[id],
);

const PATH_TO_TAB = {
  '/ai-tools/ai-room-decoration': 'room-interior',
  '/ai-tools/ai-furniture-replacement': 'room-interior',
  '/ai-tools/ai-3d-model-to-realistic-interior': 'room-interior',
  '/ai-tools/ai-exterior-design': 'exterior',
  '/ai-tools/ai-garden-design': 'exterior',
  '/ai-tools/ai-2d-floorplan-to-3d': 'floor-plan',
  '/ai-tools/ai-2d-floorplan-to-room-render': 'floor-plan',
  '/ai-tools/ai-2d-floorplan-creator': 'floor-plan',
  '/ai-tools/ai-2d-floorplan-designer': 'floor-plan',
  '/projects/ai-room-decoration': 'room-interior',
  '/projects/ai-furniture-replacement': 'room-interior',
  '/projects/ai-exterior-design': 'exterior',
  '/projects/ai-garden-design': 'exterior',
  '/projects/ai-2d-floorplan-to-3d': 'floor-plan',
  '/projects/ai-2d-floorplan-to-room-render': 'floor-plan',
  '/projects/ai-2d-floorplan-creator': 'floor-plan',
  '/projects/ai-2d-floorplan-designer': 'floor-plan',
  '/projects/new/room-redesign': 'room-interior',
  '/projects/new/exterior': 'exterior',
  '/projects/new/building-exterior': 'exterior',
  '/projects/new/garden-design': 'exterior',
  '/projects/new/floor-plan': 'floor-plan',
  '/projects/new/floor-plan-3d': 'floor-plan',
  '/projects/new/floor-plan-room-preview': 'floor-plan',
  '/projects/new/floor-plan-create': 'floor-plan',
  '/projects/new/floor-plan-designer': 'floor-plan',
};

export function getCategoryToolHub(categoryId) {
  return CATEGORY_TOOL_HUBS[categoryId] ?? null;
}

export function getVisibleCategoryTools(tools = []) {
  return tools.filter((tool) => !tool.hidden);
}

export function normalizeDesignToolTab(tab) {
  if (tab && CATEGORY_TOOL_HUBS[tab]) return tab;
  return 'room-interior';
}

export function getActiveDesignToolTab(pathname, searchParams) {
  if (pathname === '/projects/new') {
    return normalizeDesignToolTab(searchParams.get('tab'));
  }
  if (pathname.startsWith('/projects/new/room-redesign')) {
    return 'room-interior';
  }
  for (const [prefix, tabId] of Object.entries(PATH_TO_TAB)) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) {
      return tabId;
    }
  }
  return null;
}

export function getDesignToolTabHref(tabId) {
  const normalized = normalizeDesignToolTab(tabId);
  if (normalized === 'room-interior') return '/projects/new';
  return `/projects/new?tab=${normalized}`;
}
